/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useEffect, useRef } from "react";
import PageContainer from "./containers/page-container";
import { useForm } from "@tanstack/react-form";
import { Button } from "@/components/ui/button";
import { useLocation, useNavigate } from "react-router-dom";
import {
  useFetchSurveyQuestionnaire,
  useSubmitSurveyFeedback,
} from "@/hooks/use-survey";
import type { ISurveyQuestion } from "@/model/survey";
import SurveySkeleton from "./components/survey/survey-skeleton";
import LoadingIndiator from "./components/loading-indiator";
import { useNotificationError } from "@/context/error-provider";
import { ErrorNotificationType } from "./components/error-notification";
import { QueryKeys, SURVEY_QUESTION_TYPE } from "@/constants";
import SurveyResponse from "./components/survey/survey-response";
import {
  invalidateCachedQuery,
  useUpdateNotificationActionButtonName,
} from "@/hooks/use-query-client";

import RenderSurveyFormLayout from "./components/survey/survey-layout";
import SurveyTitle from "./components/survey/survey-title";
import useAuth, { AuthMode } from "@/context/auth-provider";
import useUserInfo from "@/hooks/use-user-info";
import { lowerCaseComparison } from "@/lib/string-util";
import { NoDataAvailable } from "./components/empty";

// Define the shape of the form values
type FormValues = {
  [key: `question_${number}`]: string | string[];
};

const transformToSelectedOptions = (
  formData: any,
  questions: ISurveyQuestion[],
  outgoingTrackId: string,
) => {
  const getAnswers = (question: ISurveyQuestion, value: any) => {
    const { type, options } = question;

    if (type === SURVEY_QUESTION_TYPE.PARAGRAPH) {
      return typeof value === "string" && value
        ? [
            {
              optionId: options[0]?.id,
              optionValue: "",
              optionText: value.trim(),
            },
          ]
        : [];
    }

    if (type === SURVEY_QUESTION_TYPE.CHECKBOX) {
      const answer: any = [];

      if (Array.isArray(value)) {
        value.forEach((val) => {
          const option = question.options.find(
            (opt: any) =>
              opt.option === val || (val.startsWith("Other:") && opt.isOther),
          );
          if (option) {
            answer.push({
              optionId: option.id,
              optionValue: option.label,
              optionText: val.startsWith("Other:")
                ? val.replace("Other:", "").trim()
                : option.option,
            });
          }
        });
      }
      return answer;
    }

    if (
      type === SURVEY_QUESTION_TYPE.RADIO ||
      type === SURVEY_QUESTION_TYPE.LINEAR_SCALE
    ) {
      if (typeof value === "string" && value) {
        const option = question.options.find(
          (opt: any) =>
            opt.option === value || (value.startsWith("Other:") && opt.isOther),
        );
        return option
          ? [
              {
                optionId: option.id,
                optionValue: option.label,
                optionText: value.startsWith("Other:")
                  ? value.replace("Other:", "").trim()
                  : option.option,
              },
            ]
          : [];
      } else return [];
    }
  };

  return questions.map((question: ISurveyQuestion) => {
    const value = formData[`question_${question?.id}`];
    return {
      questionId: question.id,
      outgoingTrackId,
      questionType: question.type,
      options: getAnswers(question, value),
    };
  });
};

const Survey: React.FunctionComponent<any> = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const userInfo = useUserInfo();
  const { authData } = useAuth();

  const surveyState = location.state as {
    surveyTitle?: string;
    templateId?: number;
    outgoingTrackId?: string | number | null;
  } | null;

  const outgoingTrackId = String(surveyState?.outgoingTrackId ?? "");
  const templateId = String(surveyState?.templateId ?? "");

  const { data: surveyQuestions, isLoading } = useFetchSurveyQuestionnaire(
    outgoingTrackId,
    templateId,
  );

  const surveyFeedbackQuery = useSubmitSurveyFeedback();
  const { addError } = useNotificationError();
  const updateButtonName = useUpdateNotificationActionButtonName();

  const redirectedRef = useRef(false);

  useEffect(() => {
    if (redirectedRef.current) return;
    if (!authData) return;
    const shouldRedirect =
      !surveyState || (!isLoading && !surveyQuestions?.data);
    if (shouldRedirect) {
      redirectedRef.current = true;
      addError({
        message: "Survey not available. Redirecting to home.",
        type: ErrorNotificationType.ERROR,
      });
      navigate("/home", { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading, surveyQuestions, authData]);

  const form = useForm({
    defaultValues: {}, // Initialize empty to avoid undefined errors
    onSubmit: async ({ value }) => {
      try {
        const surveyFeedback = transformToSelectedOptions(
          value,
          surveyQuestions?.data || [],
          outgoingTrackId,
        );

        // submit survey feedback to api
        surveyFeedbackQuery.mutate(surveyFeedback, {
          onSuccess: (data: any) => {
            // console.log("survey submit success >", data);
            if (!data.data.success) {
              addError({
                message: data.data.message,
                type: ErrorNotificationType.ERROR,
              });
            } else {
              addError({
                message: data.data.message,
                type: ErrorNotificationType.INFO,
              });

              //update Notification Action button name to 'ViewResponses'
              updateButtonName(
                Number(outgoingTrackId),
                Number(templateId),
                "View Responses",
              );
              // invalidate existing query after submitting the survey feedback
              invalidateCachedQuery(
                QueryKeys.FETCH_SURVEY_QUESTIONNAIRE,
                outgoingTrackId,
              );
              navigate(-1);
            }
          },
          onError: (error) => {
            console.error("Error during survery submit", error);
            addError({
              message: "Survey submission failed. Please try again.",
              type: ErrorNotificationType.ERROR,
            });
          },
        });
      } catch (error) {
        console.error("Submission Error:", error);
      }
    },
  });

  /**
   * Returns true if any value is:
   * - A non-empty string
   * - An array with at least one non-empty string
   */
  const hasUserInput = (values: Record<string, any>): boolean => {
    return Object.values(values).some((value) => {
      // Case 1: Single string value
      if (typeof value === "string") {
        return value.trim() !== "";
      }

      // Case 2: Array of strings
      if (Array.isArray(value)) {
        return value.some(
          (item) => typeof item === "string" && item.trim() !== "",
        );
      }

      return false;
    });
  };
  useEffect(() => {
    if (surveyQuestions?.data) {
      const newDefaultValues = surveyQuestions.data.reduce(
        (acc: FormValues, question: ISurveyQuestion) => {
          return {
            ...acc,
            [`question_${question.id}`]:
              question.type === SURVEY_QUESTION_TYPE.CHECKBOX ? [] : "",
          };
        },
        {},
      );

      const currentValues = form.state.values;
      // console.log(
      //   "New defaultValues:",
      //   newDefaultValues,
      //   "values >",
      //   currentValues,
      // );

      if (!hasUserInput(currentValues)) {
        form.reset({ defaultValues: newDefaultValues });
      }
    }
  }, [surveyQuestions?.data, form]);

  if (isLoading) {
    return <SurveySkeleton />;
  }

  if (!surveyState) {
    return null;
  }

  if (!surveyQuestions?.data) {
    return (
      <PageContainer title="Survey Form">
        <div className="item-center my-3 flex justify-center">
          <NoDataAvailable />
        </div>
      </PageContainer>
    );
  }

  if (surveyQuestions?.isSubmitted) {
    return (
      <PageContainer title="Survey Responses">
        {surveyQuestions?.title && surveyQuestions?.description && (
          <SurveyTitle
            title={surveyQuestions?.title}
            description={surveyQuestions?.description}
          />
        )}
        <SurveyResponse questions={surveyQuestions.data} />
      </PageContainer>
    );
  }

  return (
    <PageContainer title="Survey Form">
      {surveyQuestions?.title && surveyQuestions?.description && (
        <SurveyTitle
          title={surveyQuestions?.title}
          description={surveyQuestions?.description}
        />
      )}
      <form
        onSubmit={(e) => {
          e.preventDefault();
          e.stopPropagation();
          form.handleSubmit();
        }}
        className="m-2"
      >
        <div className="space-y-4">
          {/* 3. Map over the questions to render them */}
          {surveyQuestions?.data?.map(
            (question: ISurveyQuestion, index: number) => (
              <div
                key={`question_${question.id + index}`}
                className="my-4 rounded border p-3"
              >
                <RenderSurveyFormLayout
                  question={question}
                  questionNo={index + 1}
                  form={form}
                />
              </div>
            ),
          )}
        </div>
        {Boolean(surveyQuestions?.data) && (
          <div className="flex justify-center sm:justify-end">
            <form.Subscribe
              selector={(state) => [state.canSubmit, state.isSubmitting]}
            >
              {([canSubmit, isSubmittingForm]) => (
                <Button
                  type="submit"
                  size="lg"
                  variant="default"
                  className="my-2 w-40 bg-primary text-sm hover:bg-primary-selected sm:px-2"
                  disabled={
                    !canSubmit ||
                    surveyFeedbackQuery.isPending ||
                    isSubmittingForm ||
                    lowerCaseComparison(userInfo.mode, AuthMode.GUEST)
                  }
                >
                  <div className="flex flex-row items-center space-x-3">
                    {surveyFeedbackQuery.isPending || isSubmittingForm
                      ? "Submitting..."
                      : "Submit"}
                    {(surveyFeedbackQuery.isPending || isSubmittingForm) && (
                      <LoadingIndiator />
                    )}
                  </div>
                </Button>
              )}
            </form.Subscribe>
          </div>
        )}
      </form>
    </PageContainer>
  );
};

export default Survey;
